import { FC, useState } from 'react';
import React, { useContext } from 'react';
import { AttachedFileDto, AttachedFileSortColumn, AttachedFileWithPermissionsDto } from 'models/index';
import * as api from './FilesApi';
import * as notifications from 'lib/notifications';

import { GenericSearchWrapperWithSortOrder } from 'models';
import { isWriteableBy } from './isWriteableBy';
import { useSearchParams } from 'react-router-dom';
import { clampSortColumn, clampSortOrder } from './clampSortColumn';


interface IFilesListContext {
    files: GenericSearchWrapperWithSortOrder<AttachedFileWithPermissionsDto, AttachedFileSortColumn>;
    setFiles: (value: GenericSearchWrapperWithSortOrder<AttachedFileWithPermissionsDto, AttachedFileSortColumn>) => void;
    refreshFileList: () => void;
}


interface IFilesContext extends IFilesListContext {
    selectedFolder: AttachedFileWithPermissionsDto;
    setSelectedFolder: React.Dispatch<React.SetStateAction<AttachedFileWithPermissionsDto>>;

    parentFiles: AttachedFileDto[];
    setParentFiles: (files: AttachedFileDto[]) => void;

    selectedFolderIsWriteable: boolean;

    editedFile: AttachedFileDto;
    setEditedFile: (file: AttachedFileDto) => void;
    renameItem: (file: AttachedFileWithPermissionsDto) => void;
}

const FilesContext = React.createContext<IFilesContext>(null);

function useFilesInner() {
    const [selectedFolder, setSelectedFolder] = useState<AttachedFileWithPermissionsDto>(null);
    const [parentFiles, setParentFiles] = useState<AttachedFileDto[]>(null);

    const selectedFolderIsWriteable = selectedFolder && isWriteableBy(selectedFolder);

    // Files list
    const [files, setFiles] = useState<GenericSearchWrapperWithSortOrder<AttachedFileWithPermissionsDto, AttachedFileSortColumn>>(null);

    const [searchParams, setSearchParams] = useSearchParams({});
    const pageNrRaw = searchParams.get('page');
    const pageNr = pageNrRaw && parseInt(pageNrRaw);
    const sortColumn = clampSortColumn(searchParams.get('sortColumn')) || AttachedFileSortColumn.FileName;
    const sortOrder = clampSortOrder(searchParams.get('sortOrder'));

    function refreshFileList() {
        api.attachedFilesList({ parentId: selectedFolder?.id, sortColumn: AttachedFileSortColumn[sortColumn], sortAsc: sortOrder == 'asc', page: pageNr })
            .then(data => {
                //console.log("got data", data);
                setFiles(data);
            }).catch((reason) => {
                //console.log("catch", reason);
                notifications.dispatchFromError("Fel vid listning av filer", reason, 15);
            });
    }

    // Rename item
    const [editedFile, setEditedFile] = useState<AttachedFileDto>();
    function renameItem(value: AttachedFileDto) {
        setEditedFile(value);
    }

    return { selectedFolder, setSelectedFolder, parentFiles, setParentFiles, selectedFolderIsWriteable, files, setFiles, refreshFileList, editedFile, setEditedFile, renameItem }
}

interface FilesContextProviderProps {
    children: React.ReactNode;
}
export const FilesContextProvider: FC<FilesContextProviderProps> = (props: { children: React.ReactNode }) => {
    const value = useFilesInner();

    return <FilesContext.Provider value={value}>{props.children}</FilesContext.Provider>;
}

function useFiles() {
    return useContext(FilesContext);
}

export default useFiles;
